import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { SpacerVertical, SpacerHorizontal } from '@klarna/bubble-ui';
import { translator } from 'mage-i18n';
import {
  defaultContainerPropTypes,
  HorizontalWrapper,
  payloadUtils,
  formUtils,
  FieldsetForObject,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { validators } from '@/flow/validation';
import { CAPS } from '@/constants';
import { CallToActionButton, ContainerTemplate, BackButton, ArrayField } from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import { getSellingCountries } from '@/selectors/options';
import { preAssessmentDetails } from '@/selectors/merchant';

import { CY_SELLING_COUNTRIES_CONTAINER_IDS } from './constants';
import { getDefaultState } from './selectors';

const tPrefix = 'products-fe.containers.selling_countries';

export class SellingCountries extends React.Component {
  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.getBody = this.getBody.bind(this);
    this.getOptions = this.getOptions.bind(this);
    this.onNext = this.onNext.bind(this);

    const {
      t,
      defaultState: { sellingCountries = [] },
    } = props;

    const fields = formUtils.initForm({
      sellingCountries: {
        component: ArrayField,
        size: 1,
        label: t(`${tPrefix}.fields.selling_countries.label`),
        value: sellingCountries,
        isInitialized: false,
        isValid: false,
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.selling_countries.errors.invalid`),
          },
        ],
        extraProps: {
          options: this.getOptions(),
          clearLabel: (
            <span {...generateCyElemId(CY_SELLING_COUNTRIES_CONTAINER_IDS.BTN_CLEAR, window)}>
              {t(`${tPrefix}.clear`)}
            </span>
          ),
          selectedLabel: t(`${tPrefix}.selected.label`),
          ...generateCyElemId(CY_SELLING_COUNTRIES_CONTAINER_IDS.SELECT_SELLING_COUNTRIES, window),
        },
      },
    });

    this.state = { fields, initialState: { fields } };
  }

  onChange(fields) {
    this.setState({ fields });
  }

  getBody({ fields }) {
    const {
      selectedCountries: { main: submittedMain = undefined } = {},
    } = this.props.preAssessmentDetails;
    const all = fields.sellingCountries.value;
    const main = all.length === 1 ? all[0] : submittedMain;
    const body = { sellingCountries: { all, main } };

    return body;
  }

  getOptions() {
    const { countries } = this.props;
    return countries.map(c => ({
      key: c.key,
      label: c.label,
      ...generateCyElemId(CY_SELLING_COUNTRIES_CONTAINER_IDS.SELECTED_COUNTRY_BATCH, window),
    }));
  }

  onNext() {
    this.setState({
      ...this.state,
      fields: formUtils.initializeFields(this.state.fields),
    });

    formUtils.callToActionOnNextWrapper(this.state.fields, () => {
      this.props.onNext(
        payloadUtils.getMerchantCapabilityPayload(
          this.props.mid,
          this.state,
          this.getBody,
          'SellingCountriesContainer',
        ),
      );
    });
  }

  render() {
    const { error, hasBack, isDisabled, isLoading, onBack, t } = this.props;

    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title`)}
        subtitle={t(`${tPrefix}.subtitle`)}
        error={error}
      >
        <SpacerVertical spaceToken="space/150" />
        <FieldsetForObject
          isBorderless
          name="selling-countries"
          isDisabled={isDisabled}
          fields={this.state.fields}
          onBlur={key => formUtils.onBlurFields(key, this.state, this.onChange)}
          onChange={(key, val) => formUtils.onChangeFields(key, val, this.state, this.onChange)}
        />
        <SpacerVertical spaceToken="space/600" />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton
                onClick={onBack}
                disabled={isDisabled}
                {...generateCyElemId(CY_SELLING_COUNTRIES_CONTAINER_IDS.BTN_BACK, window)}
              >
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal spaceToken="space/300" />
            </>
          )}
          <CallToActionButton
            loading={isLoading}
            onClick={this.onNext}
            disabled={isDisabled}
            {...generateCyElemId(CY_SELLING_COUNTRIES_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

SellingCountries.propTypes = {
  ...defaultContainerPropTypes,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  defaultState: PropTypes.object,
};

export default compose(
  connect(state => ({
    countries: getSellingCountries(state),
    defaultState: getDefaultState(state),
    preAssessmentDetails: preAssessmentDetails(state),
    t: translator(state),
  })),
  withCapabilityBeforeNext({
    cap: CAPS.V2_PRE_ASSESSMENT_PATCH,
  }),
)(SellingCountries);
